

















































































































































































import { Component, Vue, Prop, Watch, Emit, Provide } from 'vue-property-decorator';
import SColumnCell from './SColumnCell.vue';
import { makeReactiveProperty, generateUID } from '@/utils/utils';
import SColumn from './SColumn.vue';
import { IFilterOptions } from './filtering';
import { ISortOptions, sort } from './sorting';
import { WatchObjectAsJson } from '@/decorators/watch-object-as-json';
import TableFiltersService from '@/components/UI/Table/table-filters-service';

interface ITableItem {
  $_selectableId?: string;
  $_expandableId?: string;
  $_additionalRowId?: string;
}

@Component({
  components: {
    SColumnCell,
  },
  i18n: {
    messages: {
      en: {
        Table: {
          noData: 'No data to display',
          noMatch: 'No results matching the search criteria',
          yes: 'Yes',
          no: 'No',
        },
      },
      nb: {
        Table: {
          noData: 'Ingen data å vise',
          noMatch: 'Ingenting oppfyller dine søkekriterier',
          yes: 'Ja',
          no: 'Nei',
        },
      },
    },
  },
})
export default class STable<Item extends ITableItem> extends Vue {
  @Prop({ type: Array, required: true }) private data!: Array<Item>;
  @Prop({
    type: Function,
    default: (item: Item, index: number) => String(index),
  }) private itemUid!: (item: Item, index: number) => string;
  @Prop() private loading?: boolean;
  @Prop(Boolean) public sticky?: boolean;
  @Prop(String) public filtersInUrl?: string;

  @Provide('sTable') private sTable = this;

  public columns: SColumn<Item>[] = [];
  public reactiveProps: { columns: SColumn<Item>[] } = { columns: [] };

  private created() {
    makeReactiveProperty(this, this.reactiveProps, 'columns', 'columns');
    if (this.filtersInUrl) {
      this.$root.$on('resetTableFilters', () => this.resetTableFilters());
    }
  }

  private mounted() {
    this.initSorting();
    this.assignSelectableIds();
    this.initExpandable();
    this.initAdditionalRow();
    this.filterData(); // needed when you set data on the table and there are no filter.
  }

  private beforeDestroy() {
    if (this.filtersInUrl) TableFiltersService.remove(this.filtersInUrl);
  }

  private get totalColumns(): number {
    return this.columns.length +
      (this.expandable ? 1 : 0) +
      ((this.sortOptions?.enabled || false) ? 1 : 0);
  }

  // FILTERING

  @Prop(Object) public filterOptions?: IFilterOptions;
  private filteredData: Array<Item> = [];
  private Object = Object; // Required to do Object.keys in template

  @WatchObjectAsJson('data')
  private filterData(): void {
    if (!this.filterOptions) {
      this.filteredData = [ ...this.data ];
      return;
    }

    if (!this.filterOptions.remoteFiltering) {
      this.filteredData = this.data.filter(item =>
        this.columns.every(column => column.filterPasses(item)),
      );
    } else {
      this.filteredData = [ ...this.data ];
    }
  }

  @Emit()
  public filterChanged(): Record<string, string> {
    this.filterData();
    return Object.fromEntries(
      this.columns
        .filter(c => c.filtering.enabled && (c.filter !== '' || !this.filterOptions!.excludeEmpty))
        .map(c => [ c.filtering.filterName, c.filter ]),
    );
  }

  public changeFiltersInUrl(): void {
    if (!this.filtersInUrl) return;

    const columnsWithNonInitialFilters = Object.fromEntries(
      this.columns
        .filter(c => c.filtering.enabled && c.filter !== c.filtering.initial)
        .map(c => [ c.filtering.filterName, c.filter ]),
    );

    if (Object.values(columnsWithNonInitialFilters).length) {
      TableFiltersService.set({[this.filtersInUrl]: columnsWithNonInitialFilters});
    } else {
      TableFiltersService.remove(this.filtersInUrl);
    }
  }

  private resetTableFilters(): void {
    this.columns.forEach(column => column.resetFilter());
  }

  // SORTING

  @Prop(Object) public sortOptions?: ISortOptions;
  private sortedAndFilteredData: Array<Item> = [ ...this.filteredData ];
  private sortByColumn: SColumn<Item> | null = null;
  public sortName = '';
  public sortDirection = '';

  private initSorting(): void {
    if (!this.sortOptions) return;

    if (this.sortOptions.initialSortName) {
      this.sortByColumn = this.columns.find(
        c => c.sorting.sortName === this.sortOptions!.initialSortName,
      ) || null;

      this.sortName = this.sortByColumn?.sorting.sortName || '';
      this.sortDirection = this.sortOptions.initialSortDirection || 'asc';
      this.sortData();
    }
  }

  private sortChanged(column: SColumn<Item>): void {
    if (this.sortByColumn !== column) {
      this.sortByColumn = column;
      this.sortName = this.sortByColumn.sorting.sortName || '';
      this.sortDirection = '';
    }

    this.sortDirection = [ 'desc', '' ].includes(this.sortDirection) ? 'asc' : 'desc';

    this.sortData();
    this.$emit('sort-changed', this.sortName, this.sortDirection);
  }

  @WatchObjectAsJson('filteredData')
  private sortData(): void {
    if (!this.sortOptions) {
      console.warn('Trying to sort when sort options are not provided.'); // eslint-disable-line no-console
      this.sortedAndFilteredData = [ ...this.filteredData ];
      return;
    }

    if (!this.sortOptions.remoteSorting && this.sortByColumn) {
      this.sortedAndFilteredData = sort(
        [ ...this.filteredData ],
        this.sortByColumn.sorting.sortGetter!,
        this.sortDirection,
      );
    } else {
      this.sortedAndFilteredData = [ ...this.filteredData ];
    }
  }

  // SELECTABLE

  @Prop(Boolean) public selectable?: boolean;
  private selectedIds: { [uid: string]: boolean } = {};

  @WatchObjectAsJson('data')
  private assignSelectableIds() {
    if (!this.selectable) return;

    this.data.forEach(item => {
      if (item.$_selectableId) return;

      item.$_selectableId = generateUID();
      this.$set(this.selectedIds, item.$_selectableId, false);
    });
  }

  private get selectedItems(): Item[] {
    return this.sortedAndFilteredData
      .filter(item => item.$_selectableId && this.selectedIds[item.$_selectableId])
      .map(item => {
        const selectedItem = { ...item };
        delete selectedItem.$_selectableId;
        delete selectedItem.$_expandableId;
        delete selectedItem.$_additionalRowId;
        return selectedItem;
      });
  }

  @Watch('selectedItems')
  @Emit('selected')
  private selectChanged(): Item[] {
    return this.selectedItems;
  }

  private get selectAll(): boolean {
    return this.sortedAndFilteredData.length > 0 &&
      this.sortedAndFilteredData
        .every(item => item.$_selectableId && this.selectedIds[item.$_selectableId]);
  }

  private set selectAll(value: boolean) {
    this.sortedAndFilteredData
      .forEach(item => {
        if (item.$_selectableId) {
          this.selectedIds[item.$_selectableId] = value;
        }
      });
  }

  // ADDITIONAL ROW

  @Prop(Boolean) public additionalRowAsShow?: boolean;
  private additionalRowIds: { [uid: string]: boolean } = {};
  private get additionalRow(): boolean {
    return !!this.$scopedSlots.additionalRow;
  }
  @Prop({
    type: Function,
    default: () => true,
  }) private showAdditionalRow!: (item: Item) => boolean;

  private initAdditionalRow() {
    this.assignAdditionalRowIds();
  }

  @WatchObjectAsJson('data')
  private assignAdditionalRowIds() {
    if (!this.additionalRow) return;

    this.data.forEach(item => {
      if (!item.$_additionalRowId) item.$_additionalRowId = generateUID();
      this.$set(this.additionalRowIds, item.$_additionalRowId, this.showAdditionalRow(item));
    });
  }

  // EXPANDABLE

  @Prop(Boolean) public expandAsShow?: boolean;
  private expandedIds: { [uid: string]: boolean } = {};
  private everExpandedIds: { [uid: string]: boolean } = {};
  private get expandable(): boolean {
    return !!this.$scopedSlots.expanded;
  }

  @Prop(Boolean) public expandedByDefault?: boolean;

  private initExpandable() {
    this.assignExpandableIds();

    if (this.expandedByDefault) this.expandAll = true;
  }

  @Prop({
    type: Function,
    default: () => false,
  }) private expandedInitialState!: (item: Item) => boolean;

  @WatchObjectAsJson('data')
  private assignExpandableIds() {
    if (!this.expandable) return;

    this.data.forEach(item => {
      if (item.$_expandableId) return;

      item.$_expandableId = generateUID();

      const initialValue = this.expandedInitialState(item);
      this.$set(this.expandedIds, item.$_expandableId, initialValue);
      this.$set(this.everExpandedIds, item.$_expandableId, initialValue);
    });
  }

  private get expandedItems(): Item[] {
    return this.sortedAndFilteredData
      .filter(item => item.$_expandableId && this.expandedIds[item.$_expandableId])
      .map(item => {
        const expandedItem = { ...item };
        delete expandedItem.$_selectableId;
        delete expandedItem.$_expandableId;
        delete expandedItem.$_additionalRowId;
        return expandedItem;
      });
  }

  @Watch('expandedItems')
  @Emit('expanded')
  private expandChanged(): Item[] {
    return this.expandedItems;
  }

  private get expandAll(): boolean {
    return this.sortedAndFilteredData.length > 0 &&
      this.sortedAndFilteredData
        .every(item => item.$_expandableId && this.expandedIds[item.$_expandableId]);
  }

  private set expandAll(value: boolean) {
    this.sortedAndFilteredData
      .forEach(item => {
        if (item.$_expandableId) {
          this.expandedIds[item.$_expandableId] = value;
          if (value) {
            this.everExpandedIds[item.$_expandableId] = value;
          }
        }
      });
  }
}
